import type { Logger } from "@expert/logging";

import { type ContextProps, useFeatures, type WorkspaceFeatures } from "@expert/features";
import { Stack } from "@mantine/core";
import { AnalyticsProvider, useReactAnalytics } from "@soluto-private/eventualize-react";
import { type GuideCommand, Timeline } from "@soluto-private/expert-workspace-timeline";
import { useMemo } from "react";

import { DisclosureModalController } from "@/components/chat/ChatSmartOffer/DisclosureModalController";
import { useSmartOfferActions } from "@/components/chat/ChatSmartOffer/useSmartOfferActions";
import { env } from "@/config";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { usePilots } from "@/pilot";
import { useProductOfferStore } from "@/sales-tools/home-product";
import { SaleProgress } from "@/sales-tools/home-product/types";
import { isChatTask, useActiveTask, useAgentFriendlyName, usePartner, useSessionId, type VoiceTask } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { userCache } from "@/shared-utils";

import { IssueSummary } from "./IssueSummary";
import classes from "./PlatformTimeline.module.css";
import { useSetupSubscriptions } from "./state";
import { features } from "./utils";

function SetupSubscriptions({ loggerWithContext }: { loggerWithContext: Logger }) {
    useSetupSubscriptions(loggerWithContext);

    return <></>;
}

export function PlatformTimeline({ guide }: { guide?: GuideCommand }) {
    const logger = useLogger({
        rootModule: "PlatformTimeline",
        supportTeam: "solve",
    });
    const task = useActiveTask();
    const isChat = isChatTask(task);
    // TODO(afd): Revisit this to properly handle nonvoice tasks and not unsafely cast this
    const callSid = (task as VoiceTask)?.agentCallId;
    const chatId = isChat ? task.chatId : undefined;
    const expertId = userCache.employeeId ?? "";
    const expertName = useAgentFriendlyName();
    const sessionId = useSessionId();
    const partner = usePartner();
    const chatTask = isChat ? task : undefined;
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const smartOfferActions = useSmartOfferActions(selectedProduct, chatTask);
    const isSaleSuccessOrDeclined =
        selectedProduct?.saleProgress === SaleProgress.Success ||
        selectedProduct?.saleProgress === SaleProgress.Declined;

    const {
        features: { isVerizonCareEnabled, isChatIssueSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { ozmo: isOzmoEnabled } = usePartnerFeatureConfigured();
    const { gaiaPayloadProps } = usePartnerConfig();

    const { pilots } = usePilots();

    const lob = pilots.includes("care") && isVerizonCareEnabled ? "care" : undefined;

    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = dispatcher
        .withIdentities({
            ...(expertId && { ExpertId: expertId }),
            ...(callSid && { CallSid: callSid }),
            ...(chatId && { ChatId: chatId }),
        })
        .withExtra({ tool: "expert-assist" });

    const featuresContext = useMemo(() => {
        return {
            appName: "expert-workspace",
            employeeId: expertId,
            environment: env.mode,
            channel: env.channel,
            partner,
            source: "expert-ui",
            lob,
        } as ContextProps;
    }, [expertId, lob, partner]);

    const IssueSummaryComponent = useMemo(() => {
        return (
            <>
                {isChat && isChatIssueSummaryEnabled && (
                    <IssueSummary activeTask={task} logger={logger} gaiaPayloadProps={gaiaPayloadProps} />
                )}
            </>
        );
    }, [isChat, isChatIssueSummaryEnabled, logger, task, gaiaPayloadProps]);

    return (
        <AnalyticsProvider dispatcher={enhancedDispatcher}>
            <Stack gap="0" className={classes.timeline}>
                <Timeline
                    gaiaPayloadProps={gaiaPayloadProps}
                    callSid={callSid}
                    chatId={chatId}
                    defaultFeatures={features}
                    expertId={expertId}
                    expertName={expertName}
                    featuresContext={featuresContext}
                    toolboxContext={{ guide, isOzmoEnabled }}
                    logger={logger}
                    lob={lob}
                    partner={partner}
                    sessionId={sessionId}
                    smartOfferActions={smartOfferActions}
                    SubscriptionsWithFeatures={isChat ? <></> : <SetupSubscriptions loggerWithContext={logger} />}
                    IssueSummaryComponent={IssueSummaryComponent}
                    isSaleSuccessOrDeclined={isSaleSuccessOrDeclined}
                />
            </Stack>
            <DisclosureModalController />
        </AnalyticsProvider>
    );
}
